import type { AgentInfo, FlashEvent, LocalAgentStatus } from "./types";

const API_BASE = "/api/v1";

export async function localAgentAgents(): Promise<AgentInfo[]> {
  try {
    const res = await fetch(`${API_BASE}/local-agent/agents`, { credentials: "include" });
    if (!res.ok) return [];
    return ((await res.json()).agents ?? []) as AgentInfo[];
  } catch {
    return [];
  }
}

export async function localAgentStatus(agentId?: string | null): Promise<LocalAgentStatus> {
  try {
    const qs = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : "";
    const res = await fetch(`${API_BASE}/local-agent/status${qs}`, {
      credentials: "include",
    });
    if (!res.ok) return { available: false, stm32: false, godex: false };
    return (await res.json()) as LocalAgentStatus;
  } catch {
    return { available: false, stm32: false, godex: false };
  }
}

export async function localAgentPrint(
  ezplB64: string,
  agentId?: string | null,
  target?: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/local-agent/print`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ezpl_b64: ezplB64,
      ...(agentId ? { agent_id: agentId } : {}),
      ...(target ? { target } : {}),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).error ?? "print failed");
  }
}

export async function localAgentPrintJobs(
  jobs: { ezpl_b64: string }[],
  delayMs: number,
  agentId?: string | null,
  target?: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/local-agent/print-jobs`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jobs,
      delay_ms: delayMs,
      ...(agentId ? { agent_id: agentId } : {}),
      ...(target ? { target } : {}),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).error ?? "print-jobs failed");
  }
}

export async function localAgentFlash(
  batchId: number,
  firmwareFilename: string,
  onEvent: (event: FlashEvent) => void,
  agentId?: string | null,
): Promise<void> {
  const res = await fetch(`${API_BASE}/local-agent/flash`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      batch_id: batchId,
      firmware_filename: firmwareFilename,
      ...(agentId ? { agent_id: agentId } : {}),
    }),
  });
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).error ?? "flash request failed");
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const block = buf.slice(0, idx).trim();
      buf = buf.slice(idx + 2);
      if (!block) continue;
      const payload = block.startsWith("data:") ? block.slice(5).trim() : block;
      try {
        onEvent(JSON.parse(payload) as FlashEvent);
      } catch {
        // skip malformed
      }
    }
  }
}
